// Server-side auth middleware for the active Supabase instance.
// Mirrors the generated requireSupabaseAuth, but validates the bearer token
// against whichever instance the current environment resolves to.
import { createMiddleware } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { supabaseConfig, selfHostedFetch } from "./self-hosted";

/**
 * Loosely typed client. The generated `Database` types make PostgREST rows
 * resolve to deeply conditional types that TanStack's serialization check
 * cannot validate, which collapses every server-function return type to `{}`.
 */
type CallerClient = SupabaseClient<any, "public", "public", any, any>;

export const requireSupabaseAuth = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const request = getRequest();
  if (!request?.headers) throw new Error("Unauthorized: No request headers available");

  const authHeader = request.headers.get("authorization");
  if (!authHeader) throw new Error("Unauthorized: No authorization header provided");
  if (!authHeader.startsWith("Bearer ")) throw new Error("Unauthorized: Only Bearer tokens are supported");

  const token = authHeader.slice("Bearer ".length);
  if (!token || token.split(".").length !== 3) throw new Error("Unauthorized: Invalid token");

  const config = supabaseConfig();
  if (!config) throw new Error("Supabase configuration is missing.");

  const anonKey = config.publishableKey;
  const supabase = createClient(config.url, anonKey, {
    global: {
      fetch: selfHostedFetch(anonKey, token),
    },
    auth: {
      storage: undefined,
      persistSession: false,
      autoRefreshToken: false,
    },
  }) as CallerClient;

  const { data, error } = await supabase.auth.getClaims(token);
  if (error || !data?.claims) throw new Error("Unauthorized: Invalid token");
  if (!data.claims.sub) throw new Error("Unauthorized: No user ID found in token");

  return next({
    context: {
      supabase,
      userId: data.claims.sub,
      claims: data.claims,
    },
  });
});
